/**
 * Helpers for recovering from a stale deployed bundle.
 *
 * Daily deploys replace wwwroot wholesale, so an open tab that lazy-loads a
 * route after a deploy requests a content-hashed chunk that no longer exists.
 * The browser surfaces that as a dynamic-import failure; the only fix is a
 * full reload, which pulls the new index.html and chunk graph.
 */

const RELOAD_GUARD_KEY = 'tma:last-stale-bundle-reload';
const RELOAD_GUARD_MS = 5 * 60 * 1000;

/** Dynamic-import failure messages across Chrome / Firefox / Safari / webpack. */
export function isStaleBundleError(error: unknown): boolean {
    const msg =
        (error as { message?: string })?.message ??
        (typeof error === 'string' ? error : '') ??
        '';

    return (
        msg.includes('Failed to fetch dynamically imported module') ||
        msg.includes('error loading dynamically imported module') ||
        msg.includes('Importing a module script failed') ||
        msg.includes('ChunkLoadError') ||
        msg.includes('Loading chunk')
    );
}

/**
 * Full-page reload, at most once per guard window. The guard lives in
 * sessionStorage so a bad server state (e.g. a cached index.html that keeps
 * referencing missing chunks) degrades to one reload every few minutes
 * instead of a reload loop.
 *
 * @param targetUrl SPA-internal URL to land on after the reload (e.g. the
 *   route the user was navigating to when the chunk load failed). Defaults to
 *   reloading the current URL.
 * @returns true if a reload was initiated.
 */
export function reloadForStaleBundle(targetUrl?: string): boolean {
    const last = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) ?? '0');
    if (Date.now() - last < RELOAD_GUARD_MS) {
        return false;
    }
    sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));

    if (targetUrl) {
        window.location.assign(targetUrl);
    } else {
        window.location.reload();
    }
    return true;
}
